import {
  Events,
  LocalStorageKey,
  localStorageUtil,
  PageLayout,
  PageLoader,
  PageNames,
} from '@diana/core';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { billingQuery } from '@diana/core/build/queries/billing';
import { Box, Stack, Typography } from '@mui/material';
import { CheckCircle } from '@diana/core/build/components/Icons/CheckCircle';
import { CHECKOUT_PAGE_NS } from './i18n';
import { useTranslation } from 'react-i18next';

enum Status {
  Open = 'open',
  Complete = 'complete',
  Error = 'error',
}

export function CheckoutReturn() {
  const { t } = useTranslation(CHECKOUT_PAGE_NS);
  const [status, setStatus] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const navigate = useNavigate();
  const getCheckoutQuery = billingQuery.getCheckoutSession();
  const [redirectUrl] = useState<string>(() => {
    const url = localStorageUtil.getItem({ key: LocalStorageKey.billingRedirectSuccess }) ?? '/';
    localStorageUtil.removeItem({ key: LocalStorageKey.billingRedirectSuccess });
    return url;
  });

  useEffect(() => {
    const sessionId = localStorageUtil.getItem({ key: LocalStorageKey.billingSessionId });
    if (!sessionId) {
      console.error('No Session ID found in local storage');
      setStatus(Status.Error);
      return;
    }

    console.info('Found Session ID:', sessionId);
    localStorageUtil.removeItem({ key: LocalStorageKey.billingSessionId });

    getCheckoutQuery.fn({ sessionId }).then((data) => {
      setStatus(data.status as Status);
      setCustomerEmail(data.customerEmail);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === Status.Complete) {
      localStorageUtil.removeItem({ key: LocalStorageKey.billingPriceId });
      Events.RefetchBillingSubscriptions.post();
      setTimeout(() => navigate(redirectUrl), 2500);
    } else if (status === Status.Error) {
      setTimeout(() => navigate(redirectUrl), 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (status === Status.Open) {
    return <Navigate to="/ecosystem/checkout" />;
  }

  return (
    <PageLayout disableSessionInfoLoader name={PageNames.CHECKOUT_RETURN}>
      {status === Status.Complete ? (
        <Stack justifyContent="center" alignItems="center" sx={{ height: '50vh' }}>
          <Box sx={{ fontSize: '4rem' }}>
            <CheckCircle fontSize="inherit" />
          </Box>
          <Typography variant="h3" sx={{ my: 1 }}>
            {t('return.paymentCompleted.title')}
          </Typography>
          <Typography variant="body1">
            {t('return.paymentCompleted.subtitle', { email: customerEmail })}
          </Typography>
        </Stack>
      ) : status === Status.Error ? (
        <Stack justifyContent="center" alignItems="center" sx={{ height: '50vh' }}>
          <Typography variant="h3" sx={{ my: 1 }}>
            {t('return.paymentError.title')}
          </Typography>
          <Typography variant="body1">{t('return.paymentError.subtitle')}</Typography>
        </Stack>
      ) : (
        <PageLoader
          isLoading={status === ''}
          backdropSx={{ background: 'transparent' }}
          text={t('return.processing.title')}
        />
      )}
    </PageLayout>
  );
}
